(() => {
  const componentName = 'yqn-coupon-popup';

  if (customElements.get(componentName)) return;

  class YQNCouponPopup extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.dialog = this.querySelector('[data-popup-dialog]');
      this.overlay = this.querySelector('[data-popup-overlay]');
      this.closeButton = this.querySelector('[data-popup-close]');
      this.copyButton = this.querySelector('[data-coupon-copy]');
      this.copyStatus = this.querySelector('[data-copy-status]');
      this.enabled = this.dataset.enabled === 'true';
      this.designMode = this.dataset.designMode === 'true';
      this.delay = Math.max(0, Number.parseInt(this.dataset.delay || '0', 10)) * 1000;
      this.closeOnOutside = this.dataset.closeOnOutside === 'true';
      this.closeOnEscape = this.dataset.closeOnEscape === 'true';
      this.isOpen = false;
      this.isClosing = false;
      this.scrollLocked = false;

      const campaign = this.dataset.campaign || 'default';
      this.storagePrefix = `yqn-coupon-popup:${campaign}`;

      this.onCloseClick = () => this.hidePopup();
      this.onOverlayClick = (event) => {
        if (event.target === this.overlay && this.closeOnOutside) this.hidePopup();
      };
      this.onKeydown = (event) => this.handleKeydown(event);
      this.onCopy = () => this.copyCoupon();
      this.onVisibilityChange = () => {
        if (!document.hidden && this.waitingForVisibility) {
          this.waitingForVisibility = false;
          this.showPopup();
        }
      };
      this.onSectionSelect = (event) => {
        if (String(event.detail?.sectionId) === String(this.dataset.sectionId)) {
          this.showPopup({ record: false });
        }
      };

      this.addEventListeners();

      if (this.designMode) {
        this.openTimer = window.setTimeout(() => this.showPopup({ record: false }), 120);
        return;
      }

      if (!this.enabled || !this.matchesPageContext() || !this.shouldShowPopup()) return;
      this.openTimer = window.setTimeout(() => this.showPopup(), this.delay);
    }

    disconnectedCallback() {
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
      window.clearTimeout(this.copyTimer);
      this.waitingForVisibility = false;
      this.hidePopup({ restoreFocus: false, immediate: true });
      this.removeEventListeners();
      this.initialized = false;
    }

    addEventListeners() {
      this.closeButton?.addEventListener('click', this.onCloseClick);
      this.overlay?.addEventListener('click', this.onOverlayClick);
      this.copyButton?.addEventListener('click', this.onCopy);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if (this.designMode) {
        document.addEventListener('shopify:section:select', this.onSectionSelect);
      }
    }

    removeEventListeners() {
      this.closeButton?.removeEventListener('click', this.onCloseClick);
      this.overlay?.removeEventListener('click', this.onOverlayClick);
      this.copyButton?.removeEventListener('click', this.onCopy);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
    }

    matchesPageContext() {
      const path = window.location.pathname;
      const pageType = this.dataset.pageType;

      switch (this.dataset.showOn) {
        case 'homepage':
          return pageType === 'index' || path === '/' || path === '';
        case 'product_pages':
          return pageType === 'product' || path.includes('/products/');
        case 'collection_pages':
          return pageType === 'collection' || path.includes('/collections/');
        default:
          return true;
      }
    }

    shouldShowPopup() {
      const frequency = this.dataset.frequency;

      if (frequency === 'once_per_session') {
        return !this.getStorage(window.sessionStorage, `${this.storagePrefix}:session`);
      }

      if (frequency === 'once_per_device') {
        const entry = this.readJSON(window.localStorage, `${this.storagePrefix}:device`);
        if (!entry?.shownAt) return true;

        const days = Math.max(1, Number.parseInt(this.dataset.expirationDays || '7', 10));
        return Date.now() - entry.shownAt >= days * 86400000;
      }

      if (frequency === 'max_times') {
        const entry = this.readJSON(window.localStorage, `${this.storagePrefix}:limited`) || {};
        const maximum = Math.max(1, Number.parseInt(this.dataset.maxShows || '1', 10));
        const repeatHours = Math.max(0, Number.parseInt(this.dataset.repeatHours || '0', 10));

        if ((entry.count || 0) >= maximum) return false;
        if (!entry.shownAt || repeatHours === 0) return true;
        return Date.now() - entry.shownAt >= repeatHours * 3600000;
      }

      return true;
    }

    showPopup({ record = true } = {}) {
      if (this.isOpen || this.isClosing) return;

      if (document.hidden && !this.designMode) {
        this.waitingForVisibility = true;
        return;
      }

      this.isOpen = true;
      this.previouslyFocusedElement = document.activeElement;
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      this.lockScroll();

      if (record) this.recordPopupShown();

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this.classList.add('is-open');
          window.requestAnimationFrame(() => {
            (this.dialog || this.closeButton)?.focus({ preventScroll: true });
          });
        });
      });
    }

    hidePopup({ restoreFocus = true, immediate = false } = {}) {
      if (!this.isOpen && !this.isClosing) return;

      this.isOpen = false;
      this.isClosing = !immediate;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.unlockScroll();
      window.clearTimeout(this.closeTimer);

      const finish = () => {
        this.hidden = true;
        this.isClosing = false;

        if (restoreFocus && this.previouslyFocusedElement instanceof HTMLElement) {
          this.previouslyFocusedElement.focus({ preventScroll: true });
        }
      };

      if (immediate) {
        finish();
      } else {
        this.closeTimer = window.setTimeout(finish, 360);
      }
    }

    handleKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape' && this.closeOnEscape) {
        event.preventDefault();
        this.hidePopup();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = Array.from(
        this.dialog?.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])') || []
      ).filter((element) => element.getClientRects().length > 0);

      if (focusable.length === 0) {
        event.preventDefault();
        this.dialog?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (document.activeElement === this.dialog) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    async copyCoupon() {
      const value = this.copyButton?.dataset.copyValue?.trim();
      if (!value) return;

      let copied = false;

      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(value);
          copied = true;
        }
      } catch (error) {
        copied = false;
      }

      if (!copied) copied = this.fallbackCopy(value);
      if (!copied) return;

      this.copyButton.classList.add('is-copied');
      this.copyStatus.textContent = this.copyButton.dataset.copiedLabel || 'Copied';
      window.clearTimeout(this.copyTimer);

      this.copyTimer = window.setTimeout(() => {
        this.copyButton?.classList.remove('is-copied');
        if (this.copyStatus) this.copyStatus.textContent = '';
      }, 2400);
    }

    fallbackCopy(value) {
      const input = document.createElement('textarea');
      input.value = value;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();

      let copied = false;
      try {
        copied = document.execCommand('copy');
      } catch (error) {
        copied = false;
      }

      input.remove();
      this.copyButton?.focus({ preventScroll: true });
      return copied;
    }

    recordPopupShown() {
      const frequency = this.dataset.frequency;
      const timestamp = Date.now();

      if (frequency === 'once_per_session') {
        this.setStorage(window.sessionStorage, `${this.storagePrefix}:session`, '1');
      } else if (frequency === 'once_per_device') {
        this.writeJSON(window.localStorage, `${this.storagePrefix}:device`, { shownAt: timestamp });
      } else if (frequency === 'max_times') {
        const key = `${this.storagePrefix}:limited`;
        const entry = this.readJSON(window.localStorage, key) || {};
        this.writeJSON(window.localStorage, key, {
          count: (entry.count || 0) + 1,
          shownAt: timestamp,
        });
      }
    }

    readJSON(storage, key) {
      const value = this.getStorage(storage, key);
      if (!value) return null;

      try {
        return JSON.parse(value);
      } catch (error) {
        return null;
      }
    }

    writeJSON(storage, key, value) {
      this.setStorage(storage, key, JSON.stringify(value));
    }

    getStorage(storage, key) {
      try {
        return storage.getItem(key);
      } catch (error) {
        return null;
      }
    }

    setStorage(storage, key, value) {
      try {
        storage.setItem(key, value);
      } catch (error) {
        // Storage can be unavailable in private or restricted browsing modes.
      }
    }

    lockScroll() {
      if (this.scrollLocked) return;
      this.scrollLocked = true;
      YQNCouponPopup.openCount += 1;
      document.body.classList.add('yqn-coupon-popup-open');
    }

    unlockScroll() {
      if (!this.scrollLocked) return;
      this.scrollLocked = false;
      YQNCouponPopup.openCount = Math.max(0, YQNCouponPopup.openCount - 1);

      if (YQNCouponPopup.openCount === 0) {
        document.body.classList.remove('yqn-coupon-popup-open');
      }
    }
  }

  YQNCouponPopup.openCount = 0;
  customElements.define(componentName, YQNCouponPopup);
})();
